import { useCallback, useEffect, useRef, useState } from 'react';

const allowedKeyInputs = new Set([
    '0',
    '1',
    '2',
    '3',
    '4',
    '5',
    '6',
    '7',
    '8',
    '9',
    'ArrowLeft',
    'ArrowRight',
    'Delete',
    'Backspace',
    'Enter',
    'Tab',
]);

const clearCurrencyFromInput = (value: string) => {
    const match = value
        .replaceAll(new RegExp(/[\s|.|,]/gi), '')
        .match(/[0-9]+/gi);
    if (match && match.length > 0) {
        return match[0];
    }
    return '';
};

const isValidNumber = (value: string) => !isNaN(Number(value));

interface SliderValueProps {
    initialFirstValue: string;
    initialLastValue: string;
    onChange?: (value: string[]) => void;
}

export const useSliderValue = ({
    initialFirstValue,
    initialLastValue,
    onChange,
}: SliderValueProps) => {
    const [firstValue, setFirstValue] = useState(initialFirstValue);
    const [lastValue, setLastValue] = useState(initialLastValue);
    const recentValues = useRef([initialFirstValue, initialLastValue]);

    const onBlurStart = useCallback(
        (value: string) => {
            // input without currency, only number
            const clearInput = clearCurrencyFromInput(value);
            if (isValidNumber(clearInput)) {
                recentValues.current = [value, lastValue];
                setFirstValue(value);
                onChange?.([clearInput, clearCurrencyFromInput(lastValue)]);
            }
        },
        [lastValue, onChange]
    );

    const onBlurEnd = useCallback(
        (value: string) => {
            // input without currency, only number
            const clearInput = clearCurrencyFromInput(value);
            if (isValidNumber(clearInput)) {
                recentValues.current = [firstValue, value];
                setLastValue(value);
                onChange?.([clearCurrencyFromInput(firstValue), clearInput]);
            }
        },
        [firstValue, onChange]
    );

    const onFocus = useCallback(() => {
        recentValues.current = [firstValue, lastValue];
    }, [firstValue, lastValue]);

    const onKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (!allowedKeyInputs.has(e.key)) {
                e.preventDefault();
                return false;
            }
            if (e.key === 'Enter') {
                (e.target as HTMLInputElement).blur();
            }
        },
        []
    );

    const onKeyUp = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Escape') {
            (e.target as HTMLInputElement).blur();
        }
    }, []);

    // sync internal values with outer
    useEffect(() => {
        setFirstValue(initialFirstValue);
        setLastValue(initialLastValue);
        recentValues.current = [initialFirstValue, initialLastValue];
    }, [initialFirstValue, initialLastValue]);

    return {
        onBlurStart,
        onBlurEnd,
        onFocus,
        onKeyDown,
        onKeyUp,
        firstValue,
        setFirstValue,
        lastValue,
        setLastValue,
        recentValues,
    };
};
